import type { Member } from '@hostesa/api-client';
import { useInviteMember, useMembers } from '@hostesa/api-client';
import { useAuth } from '@hostesa/auth-client';
import { useT } from '@hostesa/i18n';
import {
  Avatar,
  AvatarFallback,
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Input,
  Label,
  LoadingState,
  PageHeader,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@hostesa/ui';
import { useForm } from '@tanstack/react-form';
import { useState } from 'react';
import { z } from 'zod';
import { useHotelContext } from '../../lib/hotel-context';

// ── Helpers ──────────────────────────────────────────────────────────────────

const ROLE_LABEL_KEYS: Record<Member['role'], string> = {
  hotel_admin: 'roles.hotel_admin',
  hotel_staff: 'roles.hotel_staff',
  tenant_admin: 'roles.tenant_admin',
};

/** A role's badge variant — tenant/hotel admins read as elevated. */
function roleVariant(role: Member['role']): 'default' | 'secondary' {
  return role === 'hotel_staff' ? 'secondary' : 'default';
}

/** Two-letter initials from a display name (falls back to the email local-part). */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return (parts[0]?.slice(0, 2) ?? '?').toUpperCase();
  return `${parts[0]?.[0] ?? ''}${parts[parts.length - 1]?.[0] ?? ''}`.toUpperCase();
}

// ── Invite member sheet ──────────────────────────────────────────────────────

interface InviteMemberSheetProps {
  tenantId: string;
  hotelId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function InviteMemberSheet({ tenantId, hotelId, open, onOpenChange }: InviteMemberSheetProps) {
  const { t } = useT('team');
  const inviteMember = useInviteMember(tenantId);
  const [formError, setFormError] = useState<string | null>(null);

  const emailSchema = z.string().min(1, t('invite.emailRequired')).email(t('invite.emailInvalid'));
  const passwordSchema = z
    .string()
    .min(1, t('invite.passwordRequired'))
    .min(8, t('invite.passwordTooShort'));

  const form = useForm({
    defaultValues: {
      email: '',
      fullName: '',
      password: '',
      role: 'hotel_staff' as 'hotel_admin' | 'hotel_staff',
    },
    onSubmit: async ({ value }) => {
      setFormError(null);
      try {
        // NON-optimistic: grants access — await the backend, then the list refreshes.
        await inviteMember.mutateAsync({
          email: value.email,
          fullName: value.fullName || undefined,
          password: value.password,
          role: value.role,
          hotelId,
        });
        onOpenChange(false);
      } catch {
        setFormError(t('invite.error'));
      }
    },
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent data-testid="team-invite-sheet">
        <SheetHeader>
          <SheetTitle>{t('invite.title')}</SheetTitle>
          <SheetDescription>{t('invite.description')}</SheetDescription>
        </SheetHeader>
        <form
          data-testid="team-invite-form"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void form.handleSubmit();
          }}
          className="flex flex-col gap-4 px-6"
        >
          <form.Field
            name="email"
            validators={{ onSubmit: emailSchema }}
            // biome-ignore lint/correctness/noChildrenProp: TanStack Form field render-prop API
            children={(fieldApi) => (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="team-invite-email">{t('invite.emailLabel')}</Label>
                <Input
                  id="team-invite-email"
                  data-testid="team-invite-email-input"
                  type="email"
                  placeholder={t('invite.emailPlaceholder')}
                  value={fieldApi.state.value}
                  onBlur={fieldApi.handleBlur}
                  onChange={(e) => fieldApi.handleChange(e.target.value)}
                  aria-invalid={fieldApi.state.meta.errors.length > 0}
                />
                {fieldApi.state.meta.errors.length > 0 && (
                  <p
                    data-testid="team-invite-email-error"
                    role="alert"
                    className="text-xs text-[var(--color-alert)]"
                  >
                    {String(
                      fieldApi.state.meta.errors[0]?.message ?? fieldApi.state.meta.errors[0],
                    )}
                  </p>
                )}
              </div>
            )}
          />

          <form.Field
            name="fullName"
            // biome-ignore lint/correctness/noChildrenProp: TanStack Form field render-prop API
            children={(fieldApi) => (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="team-invite-full-name">{t('invite.fullNameLabel')}</Label>
                <Input
                  id="team-invite-full-name"
                  data-testid="team-invite-full-name-input"
                  placeholder={t('invite.fullNamePlaceholder')}
                  value={fieldApi.state.value}
                  onBlur={fieldApi.handleBlur}
                  onChange={(e) => fieldApi.handleChange(e.target.value)}
                />
              </div>
            )}
          />

          <form.Field
            name="password"
            validators={{ onSubmit: passwordSchema }}
            // biome-ignore lint/correctness/noChildrenProp: TanStack Form field render-prop API
            children={(fieldApi) => (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="team-invite-password">{t('invite.passwordLabel')}</Label>
                <Input
                  id="team-invite-password"
                  data-testid="team-invite-password-input"
                  type="password"
                  autoComplete="new-password"
                  placeholder={t('invite.passwordPlaceholder')}
                  value={fieldApi.state.value}
                  onBlur={fieldApi.handleBlur}
                  onChange={(e) => fieldApi.handleChange(e.target.value)}
                  aria-invalid={fieldApi.state.meta.errors.length > 0}
                />
                {fieldApi.state.meta.errors.length > 0 && (
                  <p
                    data-testid="team-invite-password-error"
                    role="alert"
                    className="text-xs text-[var(--color-alert)]"
                  >
                    {String(
                      fieldApi.state.meta.errors[0]?.message ?? fieldApi.state.meta.errors[0],
                    )}
                  </p>
                )}
              </div>
            )}
          />

          <form.Field
            name="role"
            // biome-ignore lint/correctness/noChildrenProp: TanStack Form field render-prop API
            children={(fieldApi) => (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="team-invite-role">{t('invite.roleLabel')}</Label>
                <select
                  id="team-invite-role"
                  data-testid="team-invite-role-select"
                  value={fieldApi.state.value}
                  onChange={(e) =>
                    fieldApi.handleChange(e.target.value as 'hotel_admin' | 'hotel_staff')
                  }
                  className="h-9 rounded-md border border-[var(--color-warm-stone)] bg-[var(--color-surface)] px-3 text-sm text-[var(--color-charcoal)]"
                >
                  <option value="hotel_staff">{t('roles.hotel_staff')}</option>
                  <option value="hotel_admin">{t('roles.hotel_admin')}</option>
                </select>
              </div>
            )}
          />

          {formError && (
            <p
              data-testid="team-invite-form-error"
              role="alert"
              className="text-sm text-[var(--color-alert)]"
            >
              {formError}
            </p>
          )}

          <SheetFooter className="px-0">
            <Button
              type="button"
              variant="ghost"
              data-testid="team-invite-cancel-btn"
              onClick={() => onOpenChange(false)}
            >
              {t('invite.cancel')}
            </Button>
            <form.Subscribe
              selector={(state) => state.isSubmitting}
              // biome-ignore lint/correctness/noChildrenProp: TanStack Form Subscribe render-prop API
              children={(isSubmitting) => (
                <Button type="submit" data-testid="team-invite-submit-btn" disabled={isSubmitting}>
                  {isSubmitting ? t('invite.submitting') : t('invite.submit')}
                </Button>
              )}
            />
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}

// ── Roster table ─────────────────────────────────────────────────────────────

function TeamTable({ members }: { members: readonly Member[] }) {
  const { t } = useT('team');

  return (
    <Table data-testid="team-table">
      <TableHeader>
        <TableRow>
          <TableHead>{t('table.nameHeading')}</TableHead>
          <TableHead>{t('table.roleHeading')}</TableHead>
          <TableHead>{t('table.statusHeading')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {members.map((member) => {
          const fullName = typeof member.fullName === 'string' ? member.fullName.trim() : '';
          const name = fullName || t('unnamed');
          const email = (typeof member.email === 'string' && member.email) || t('noEmail');
          const status = member.status ?? 'active';
          return (
            <TableRow key={member.userId} data-testid={`team-row-${member.userId}`}>
              <TableCell>
                <div className="flex items-center gap-3">
                  <Avatar data-testid={`team-avatar-${member.userId}`}>
                    <AvatarFallback>{initials(fullName || email)}</AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-[var(--color-charcoal)]">{name}</span>
                    <span className="text-xs text-[var(--color-stone-mid)]">{email}</span>
                  </div>
                </div>
              </TableCell>
              <TableCell>
                <Badge
                  data-testid={`team-role-${member.userId}`}
                  variant={roleVariant(member.role)}
                >
                  {t(ROLE_LABEL_KEYS[member.role])}
                </Badge>
              </TableCell>
              <TableCell>
                <StatusBadge
                  data-testid={`team-status-${member.userId}`}
                  status={status === 'active' ? 'active' : 'inactive'}
                  label={status === 'active' ? t('status.active') : t('status.suspended')}
                />
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function TeamPage() {
  const { t } = useT('team');
  const { tenantId, hotelId } = useHotelContext();
  const { can } = useAuth();
  const canManage = can('user:manage');
  const members = useMembers(tenantId);
  const [inviteOpen, setInviteOpen] = useState(false);

  const count = members.data?.length ?? 0;

  return (
    <div data-testid="team-page" className="flex flex-col gap-6">
      <PageHeader
        data-testid="team-page-header"
        eyebrow={t('eyebrow')}
        title={t('pageTitle')}
        description={t('pageDescription')}
        actions={
          <div className="flex items-center gap-3">
            <span
              data-testid="team-members-figure"
              className="font-[var(--font-mono)] text-xs font-medium uppercase tracking-widest text-[var(--color-stone-mid)]"
            >
              {t('membersFigure')} {count}
            </span>
            {canManage ? (
              <Button
                type="button"
                data-testid="team-invite-btn"
                onClick={() => setInviteOpen(true)}
              >
                {t('inviteAction')}
              </Button>
            ) : (
              <span
                data-testid="team-manage-disabled-hint"
                className="text-xs text-[var(--color-stone-mid)]"
              >
                {t('manageDisabledHint')}
              </span>
            )}
          </div>
        }
      />

      {members.isLoading && <LoadingState data-testid="team-loading" title={t('states.loading')} />}
      {members.isError && (
        <ErrorState
          data-testid="team-error"
          title={t('states.errorTitle')}
          description={t('states.errorDescription')}
          action={
            <Button
              type="button"
              data-testid="team-retry-btn"
              onClick={() => void members.refetch()}
            >
              {t('states.retry')}
            </Button>
          }
        />
      )}
      {members.isSuccess &&
        (members.data.length === 0 ? (
          <EmptyState
            data-testid="team-empty"
            title={t('states.emptyTitle')}
            description={t('states.emptyDescription')}
          />
        ) : (
          <TeamTable members={members.data} />
        ))}

      <InviteMemberSheet
        tenantId={tenantId}
        hotelId={hotelId}
        open={inviteOpen}
        onOpenChange={setInviteOpen}
      />
    </div>
  );
}
